import { Ref, ref, computed, inject } from "vue";
import { useStore } from "vuex";
import { NAMESPACE, EDITOR_MODE } from "@modules/editor/service/editor.constant";
import { isEmpty } from "@utils/var.check";
import { LESSON_ACTIVITY_TYPE } from "@plugins/common/constant/resource.constant";

interface Props {
  isNewQuestion: boolean;
  question: Question;
  gradingRubricUuid?: string;
  showMarks?: boolean;
  editingRubric?: boolean;
  hasActiveSubQuestion?: boolean;
  showSpeechEvaluationRubric?: boolean;
  removeSpeechEvaluationRubric?: boolean;
  speechEvaluationLanguage?: string;
  mode: string;
  message?: string;
  isQuizGradingRubric?: boolean;
  quiz?: Quiz;
  sortedQuizQuestionData?: object[];
  isAttempted?: boolean;
  isFromSearchResourcePreview?: boolean;
  dirtyRubricId?: string;
  rubrics?: Ref<Rubric>;
  speechEvaluationRubric?: boolean;
  isIsrQuestion?: boolean;
  hasSelectedHtml5Question?: boolean;
}

interface ComponentReference {
  isProcessing: Ref<boolean>;
  getProcessingTriggerElement: Ref<string | undefined>;
}

interface ResourceContext {
  id: number;
  lacId: number;
  isInQuiz: boolean;
  isPaginated: boolean;
}

interface Question {
  uuid: string;
}

interface Quiz {
  id: number;
}

interface Rubric {
  uuid: string;
}

export function useGradingRubric(props: Props, componentRefs: ComponentReference) {
  const store = useStore();
  const resourceContext: ResourceContext | undefined = inject("resourceContext");

  // Reactive state
  const isAddNewRubric = ref(false);
  const isRubricAddedFromLibraryOrStarred = ref(false);
  const isRubricAddedFromAcp = ref(false);
  const selectedRubricUuidFromLibraryOrStarred = ref("");
  const rubricAddedFromLibraryOrStarred = ref<object>({});
  const rubricAddedFromAcp = ref({});

  // Computed properties
  const lesson = computed(() => store.getters[`${NAMESPACE.LESSON}/getLesson`]);
  const getEditingComponentId = computed(
    () => store.getters[`${NAMESPACE.LESSON}/getEditingComponentId`],
  );
  const getActivity = computed(() => store.getters[`${NAMESPACE.LESSON}/getActivity`]);
  const currentPageId = computed(() => store.getters[`${NAMESPACE.LESSON}/currentPageId`]);
  const getQuizRubric = computed(() => store.getters[`${NAMESPACE.LESSON}/getQuizRubric`]);
  const getRubric = computed(() => store.getters[`${NAMESPACE.COMPONENT}/getRubric`]);
  const isEditorInViewMode = computed(
    () => store.getters[`${NAMESPACE.EDITOR}/isEditorInViewMode`],
  );
  const isEditorInEditMode = computed(
    () => store.getters[`${NAMESPACE.EDITOR}/isEditorInEditMode`],
  );
  const getEditingResourceUuid = computed(
    () => store.getters[`${NAMESPACE.COMPONENT}/getEditingResourceUuid`],
  );

  const isAccordionOpen = computed(() => {
    if (props.isFromSearchResourcePreview || props.isQuizGradingRubric) {
      return false;
    }
    return isParentEdited.value;
  });

  const isParentEdited = computed(() => {
    return (
      (getEditingComponentId.value === lacId.value && getEditingResourceUuid.value === 0) ||
      getEditingResourceUuid.value === props.question.uuid ||
      (isActivityQuiz.value &&
        getEditingComponentId.value === "newComponent" &&
        getEditingComponentId.value === lacId.value)
    );
  });

  const isActivityQuiz = computed(() => {
    return getActivity.value(currentPageId.value).type === LESSON_ACTIVITY_TYPE.ACTIVITY_QUIZ;
  });

  const rubric = computed(() => {
    if (!props.gradingRubricUuid) {
      return {};
    }

    if (isRubricAddedFromLibraryOrStarred.value) return rubricAddedFromLibraryOrStarred.value;
    if (isRubricAddedFromAcp.value) return rubricAddedFromAcp.value;
    if (props.isQuizGradingRubric) {
      return getQuizRubric.value(props.gradingRubricUuid);
    } else {
      return getRubric.value(props.gradingRubricUuid);
    }
  });

  const id = computed(() => {
    return props.isQuizGradingRubric ? props.quiz?.id : resourceContext?.id;
  });

  const lacId = computed(() => {
    return resourceContext?.lacId;
  });

  const processingId = computed(() => {
    return id.value;
  });

  const dirtyId: Ref<string> = computed(() => {
    return props.dirtyRubricId || id.value?.toString() || "";
  });

  const isResource = computed(() => {
    return true;
  });

  const resourceUuid = computed(() => {
    if ((!hasGradingRubric.value && isAddNewRubric.value) || isNewComponent.value) {
      return "newRubric";
    }
    return rubric.value?.uuid || props.rubrics?.value.uuid;
  });

  const isViewMode = computed(() => {
    return (
      props.mode === EDITOR_MODE.VIEW ||
      props.mode === EDITOR_MODE.ACTIVE_PREEDIT ||
      props.mode === EDITOR_MODE.INACTIVE_PREEDIT
    );
  });

  const isEditMode = computed(() => {
    return (
      props.mode === EDITOR_MODE.EDIT ||
      props.mode === EDITOR_MODE.ACTIVE_EDIT ||
      props.mode === EDITOR_MODE.INACTIVE_EDIT
    );
  });

  const isEnableAdd = computed(() => {
    return isNewComponent.value || !isDisable.value;
  });

  const hasGradingRubric = computed(() => {
    return !isEmpty(rubric.value) || props.gradingRubricUuid === "newRubric";
  });

  const containerCss = computed(() => {
    return Object.assign(wrapperClass.value, {
      empty: !hasGradingRubric.value && !isAddNewRubric.value,
    });
  });

  const wrapperClass = computed(() => {
    return {
      view: isEditorInViewMode.value,
      edit: isEditorInEditMode.value,
      editing: isEditMode.value,
    };
  });

  const isDisable = computed(() => {
    return (
      componentRefs.isProcessing.value ||
      props.mode === EDITOR_MODE.INACTIVE_PREEDIT ||
      componentRefs.getProcessingTriggerElement.value === "saveRubrics" ||
      props.isAttempted
    );
  });

  const isRubricInputDisabled = computed(() => {
    return (
      componentRefs.isProcessing.value ||
      componentRefs.getProcessingTriggerElement.value === "saveRubrics" ||
      props.speechEvaluationRubric
    );
  });

  const isNewComponent = computed(() => {
    return props.isNewQuestion || isAddNewRubric.value;
  });

  const isIsrQuestion = computed(() => props.isIsrQuestion === true);

  const hasSelectedHtml5Question = computed(() => props.hasSelectedHtml5Question === true);

  const showAddRubricButton = computed(() => {
    if (isIsrQuestion.value) {
      return hasSelectedHtml5Question.value;
    }
    return !hasGradingRubric.value && !isAddNewRubric.value && isEditMode.value;
  });

  // Actions
  const updateComponentRubric = (...args) =>
    store.dispatch(`${NAMESPACE.LESSON}/updateComponentRubric`, ...args);
  const setEditingComponentId = (...args) =>
    store.dispatch(`${NAMESPACE.LESSON}/setEditingComponentId`, ...args);
  const resetEditingComponentId = (...args) =>
    store.dispatch(`${NAMESPACE.LESSON}/resetEditingComponentId`, ...args);
  const createQuizGradingRubric = (...args) =>
    store.dispatch(`${NAMESPACE.LESSON}/createQuizGradingRubric`, ...args);
  const saveQuizGradingRubric = (...args) =>
    store.dispatch(`${NAMESPACE.LESSON}/saveQuizGradingRubric`, ...args);
  const deleteQuizGradingRubric = (...args) =>
    store.dispatch(`${NAMESPACE.LESSON}/deleteQuizGradingRubric`, ...args);
  const updateActivityRubric = (...args) =>
    store.dispatch(`${NAMESPACE.LESSON}/updateActivityRubric`, ...args);
  const removeActivityRubric = (...args) =>
    store.dispatch(`${NAMESPACE.LESSON}/removeActivityRubric`, ...args);
  const createGradingRubric = (...args) =>
    store.dispatch(`${NAMESPACE.COMPONENT}/createGradingRubric`, ...args);
  const saveGradingRubric = (...args) =>
    store.dispatch(`${NAMESPACE.COMPONENT}/saveGradingRubric`, ...args);
  const deleteGradingRubric = (...args) =>
    store.dispatch(`${NAMESPACE.COMPONENT}/deleteGradingRubric`, ...args);
  const updateQuestionRubric = (...args) =>
    store.dispatch(`${NAMESPACE.COMPONENT}/updateQuestionRubric`, ...args);
  const updateQuestionMarks = (...args) =>
    store.dispatch(`${NAMESPACE.COMPONENT}/updateQuestionMarks`, ...args);
  const setEditingResourceUuid = (...args) =>
    store.dispatch(`${NAMESPACE.COMPONENT}/setEditingResourceUuid`, ...args);
  const resetEditingResourceUuid = (...args) =>
    store.dispatch(`${NAMESPACE.COMPONENT}/resetEditingResourceUuid`, ...args);

  return {
    resourceContext,
    // State
    isAddNewRubric,
    isRubricAddedFromLibraryOrStarred,
    isRubricAddedFromAcp,
    selectedRubricUuidFromLibraryOrStarred,
    rubricAddedFromLibraryOrStarred,
    rubricAddedFromAcp,

    // Computed
    lesson,
    getEditingComponentId,
    getActivity,
    currentPageId,
    getQuizRubric,
    getRubric,
    getEditingResourceUuid,
    isAccordionOpen,
    isParentEdited,
    isActivityQuiz,
    rubric,
    id,
    lacId,
    processingId,
    dirtyId,
    isResource,
    resourceUuid,
    isViewMode,
    isEditMode,
    isEnableAdd,
    hasGradingRubric,
    containerCss,
    wrapperClass,
    isDisable,
    isRubricInputDisabled,
    isNewComponent,
    showAddRubricButton,

    // Actions
    updateComponentRubric,
    setEditingComponentId,
    resetEditingComponentId,
    createQuizGradingRubric,
    saveQuizGradingRubric,
    deleteQuizGradingRubric,
    updateActivityRubric,
    removeActivityRubric,
    createGradingRubric,
    saveGradingRubric,
    deleteGradingRubric,
    updateQuestionRubric,
    updateQuestionMarks,
    setEditingResourceUuid,
    resetEditingResourceUuid,
  };
}
